import React, { useEffect, useState } from 'react';
import { walletAPI, orgAPI } from '@/services/api';
import { paymentsAPI } from '@/services/paymentsAPI';
import ScrollHint from '@/components/shared/ScrollHint';
import RazorpayCheckoutModal from '@/components/shared/RazorpayCheckoutModal';
import { useAuthStore } from '@/store/authStore';
import { Coins, Download, ArrowUpRight, ArrowDownRight, Send, Loader2, Crown, AlertCircle, Plus, CreditCard, IndianRupee } from 'lucide-react';

function StatusPill({ kind }) {
  return kind === 'credit'
    ? <span className="inline-flex items-center gap-1 text-[10px] uppercase tracking-wider px-2 py-0.5 rounded-full bg-emerald-500/10 text-emerald-600 dark:text-emerald-400"><ArrowUpRight className="w-3 h-3" /> Credit</span>
    : <span className="inline-flex items-center gap-1 text-[10px] uppercase tracking-wider px-2 py-0.5 rounded-full bg-red-500/10 text-red-600 dark:text-red-400"><ArrowDownRight className="w-3 h-3" /> Debit</span>;
}

export default function RecruiterWallet() {
  const user = useAuthStore((s) => s.user);
  const [me, setMe] = useState(null);
  const [txns, setTxns] = useState([]);
  const [subs, setSubs] = useState([]);
  const [filters, setFilters] = useState({ start_date: '', end_date: '', kind: '' });
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [transferState, setTransferState] = useState({ open: false, user: null, amount: '' });
  const [rechargeOpen, setRechargeOpen] = useState(false);
  const [payments, setPayments] = useState([]);

  const load = async () => {
    setLoading(true);
    setError(null);
    try {
      const m = await walletAPI.me();
      setMe(m);
      const t = await walletAPI.myTransactions({ ...filters, limit: 100 });
      setTxns(t.transactions || []);
      try {
        const ph = await paymentsAPI.getHistory();
        setPayments(ph?.records || []);
      } catch {/* ignore */}
      try {
        const ctx = await orgAPI.getContext();
        if (ctx.is_head) {
          const s = await walletAPI.headSubordinates();
          setSubs(s.wallets || []);
        }
      } catch {/* not a head */}
    } catch (e) {
      setError(e.response?.data?.detail || e.message);
    }
    setLoading(false);
  };

  useEffect(() => { load();}, []);

  const apply = (e) => { e.preventDefault(); load(); };
  const dl = async () => { try { await walletAPI.downloadMyCsv(filters); } catch (e) { setError(e.response?.data?.detail || e.message); } };

  const doTransfer = async () => {
    const { user, amount } = transferState;
    const n = parseInt(amount, 10);
    if (!n || n <= 0) return;
    try {
      await walletAPI.headTransfer(user.user_id, n, '');
      window.dispatchEvent(new Event('mu-wallet-changed'));
      setTransferState({ open: false, user: null, amount: '' });
      load();
    } catch (e) {
      setError(e.response?.data?.detail || e.message);
    }
  };

  if (loading && !me) return <div className="text-slate-500 dark:text-zinc-400 text-sm" data-testid="recruiter-wallet-loading">Loading wallet…</div>;
  if (!me) return <div className="text-red-600 dark:text-red-400 text-sm">{error || 'Wallet unavailable'}</div>;

  const isHead = subs.length > 0;

  return (
    <div data-testid="recruiter-wallet">
      <div className="mb-6">
        <h1 className="text-2xl font-semibold text-slate-900 dark:text-white tracking-tight flex items-center gap-2"><Coins className="w-5 h-5 text-amber-600 dark:text-amber-400" /> Mu Points Wallet</h1>
        <p className="text-slate-500 dark:text-zinc-500 text-sm">Track your balance, transactions, and {isHead ? 'allocate points to your team.' : 'see how each job posting consumed points.'}</p>
      </div>

      {error && <div className="mb-4 px-4 py-2 rounded bg-red-500/10 text-red-600 dark:text-red-400 text-sm flex items-center gap-2"><AlertCircle className="w-4 h-4" />{error}</div>}

      <div className="grid grid-cols-1 lg:grid-cols-3 gap-4 mb-6">
        <div className="bg-gradient-to-br from-amber-500/10 to-amber-500/5 border border-amber-500/20 rounded-xl p-5">
          <div className="flex items-start justify-between gap-2">
            <div>
              <p className="text-amber-700 dark:text-amber-300 text-xs uppercase tracking-wider mb-1">Balance</p>
              <p className="text-3xl font-semibold text-slate-900 dark:text-white" data-testid="wallet-balance">{me.balance} <span className="text-slate-500 dark:text-zinc-500 text-base font-normal">MP</span></p>
              {me.balance < 0 && <p className="text-red-600 dark:text-red-400 text-xs mt-2">Negative — top up to keep posting</p>}
              {me.balance >= 0 && me.balance < 20 && <p className="text-amber-600 dark:text-amber-400 text-xs mt-2">Low balance — top up soon</p>}
            </div>
            <button
              onClick={() => setRechargeOpen(true)}
              className="px-3 py-1.5 rounded-lg bg-amber-500 hover:bg-amber-600 text-white text-xs font-medium flex items-center gap-1 shadow"
              data-testid="wallet-recharge-btn"
            >
              <Plus className="w-3.5 h-3.5" /> Recharge
            </button>
          </div>
        </div>
        <div className="bg-white dark:bg-[#111114] border border-slate-200 dark:border-zinc-800/50 rounded-xl p-5 col-span-2">
          <p className="text-slate-500 dark:text-zinc-400 text-xs uppercase tracking-wider mb-2">Pricing (this org)</p>
          <div className="grid grid-cols-2 sm:grid-cols-4 gap-3 text-xs">
            {Object.entries(me.pricing).map(([k, v]) => (
              <div key={k} className="bg-slate-50 dark:bg-[#0a0a0b] border border-slate-200 dark:border-zinc-800 rounded-lg p-2.5">
                <p className="text-slate-500 dark:text-zinc-500 capitalize">{k.replace(/_/g, ' ')}</p>
                <p className="text-slate-900 dark:text-white font-semibold mt-0.5">{v} MP</p>
              </div>
            ))}
          </div>
        </div>
      </div>

      {/* Head: assign to subordinates */}
      {isHead && (
        <section className="mb-6" data-testid="head-subordinates-section">
          <h2 className="text-sm font-semibold text-slate-600 dark:text-zinc-300 uppercase tracking-wider mb-2 flex items-center gap-2"><Crown className="w-3.5 h-3.5 text-amber-600 dark:text-amber-400" /> Team balances</h2>
          <div className="space-y-2">
            {subs.map(s => (
              <div key={s.user_id} className="bg-white dark:bg-[#111114] border border-slate-200 dark:border-zinc-800/50 rounded-lg p-3 flex items-center justify-between" data-testid={`sub-row-${s.user_id}`}>
                <div className="flex items-center gap-3">
                  {s.is_head && <Crown className="w-4 h-4 text-amber-600 dark:text-amber-400" />}
                  <div>
                    <p className="text-slate-900 dark:text-white text-sm font-medium">{s.name || s.email.split('@')[0]} <span className="text-slate-500 dark:text-zinc-500 text-xs">· {s.email}</span></p>
                    <p className="text-slate-500 dark:text-zinc-500 text-xs">{s.member_status} {s.is_head && '· Recruiter Head'}</p>
                  </div>
                </div>
                <div className="flex items-center gap-3">
                  <span className="text-slate-700 dark:text-zinc-200 font-medium" data-testid={`sub-balance-${s.user_id}`}>{s.balance} MP</span>
                  {!s.is_head && s.member_status === 'active' && (
                    <button onClick={() => setTransferState({ open: true, user: s, amount: '' })} className="px-3 py-1.5 bg-emerald-500/10 text-emerald-600 dark:text-emerald-400 hover:bg-emerald-500/20 rounded-lg text-xs font-medium flex items-center gap-1.5" data-testid={`transfer-btn-${s.user_id}`}><Send className="w-3 h-3" /> Send MP</button>
                  )}
                </div>
              </div>
            ))}
          </div>
        </section>
      )}

      {/* Transactions */}
      <section data-testid="transactions-section">
        <div className="flex items-center justify-between mb-2">
          <h2 className="text-sm font-semibold text-slate-600 dark:text-zinc-300 uppercase tracking-wider">Transactions</h2>
          <button onClick={dl} className="flex items-center gap-1.5 text-xs text-slate-500 dark:text-zinc-400 hover:text-slate-900 dark:hover:text-white px-3 py-1.5 bg-slate-100 dark:bg-zinc-800/60 hover:bg-slate-100 dark:hover:bg-zinc-800 rounded-lg" data-testid="download-txns-csv-btn"><Download className="w-3.5 h-3.5" /> CSV</button>
        </div>
        <form onSubmit={apply} className="bg-white dark:bg-[#111114] border border-slate-200 dark:border-zinc-800/50 rounded-lg p-3 flex flex-wrap gap-2 mb-3 text-xs">
          <input type="date" value={filters.start_date} onChange={e => setFilters({ ...filters, start_date: e.target.value })} className="bg-slate-50 dark:bg-[#0a0a0b] border border-slate-200 dark:border-zinc-800 rounded px-2 py-1 text-slate-900 dark:text-white" data-testid="txn-filter-start" />
          <input type="date" value={filters.end_date} onChange={e => setFilters({ ...filters, end_date: e.target.value })} className="bg-slate-50 dark:bg-[#0a0a0b] border border-slate-200 dark:border-zinc-800 rounded px-2 py-1 text-slate-900 dark:text-white" data-testid="txn-filter-end" />
          <select value={filters.kind} onChange={e => setFilters({ ...filters, kind: e.target.value })} className="bg-slate-50 dark:bg-[#0a0a0b] border border-slate-200 dark:border-zinc-800 rounded px-2 py-1 text-slate-900 dark:text-white" data-testid="txn-filter-kind">
            <option value="">All</option>
            <option value="credit">Credit</option>
            <option value="debit">Debit</option>
          </select>
          <button type="submit" className="px-3 py-1 bg-slate-900 dark:bg-white text-white dark:text-black rounded font-medium" data-testid="apply-txn-filters">Apply</button>
        </form>

        <ScrollHint>
        <div className="overflow-x-auto bg-white dark:bg-[#111114] border border-slate-200 dark:border-zinc-800/50 rounded-lg">
          <table className="w-full text-xs min-w-[720px]">
            <thead className="border-b border-slate-200 dark:border-zinc-800/70">
              <tr className="text-slate-500 dark:text-zinc-500 text-left">
                <th className="px-3 py-2 font-medium">Date</th>
                <th className="px-3 py-2 font-medium">Kind</th>
                <th className="px-3 py-2 font-medium">Amount</th>
                <th className="px-3 py-2 font-medium">Reason</th>
                <th className="px-3 py-2 font-medium text-right">Balance after</th>
              </tr>
            </thead>
            <tbody>
              {txns.length === 0 ? (
                <tr><td colSpan="5" className="text-slate-500 dark:text-zinc-500 text-center py-6">No transactions in range.</td></tr>
              ) : txns.map(t => (
                <tr key={t.id} className="border-b border-slate-200 dark:border-zinc-900/60" data-testid={`txn-row-${t.id}`}>
                  <td className="px-3 py-2 text-slate-500 dark:text-zinc-400 whitespace-nowrap">{(t.created_at || '').slice(0, 16).replace('T', ' ')}</td>
                  <td className="px-3 py-2"><StatusPill kind={t.kind} /></td>
                  <td className={`px-3 py-2 font-medium ${t.kind === 'credit' ? 'text-emerald-600 dark:text-emerald-400' : 'text-red-600 dark:text-red-400'}`}>{t.kind === 'credit' ? '+' : '-'}{t.amount}</td>
                  <td className="px-3 py-2 text-slate-600 dark:text-zinc-300">{t.reason}</td>
                  <td className="px-3 py-2 text-slate-700 dark:text-zinc-200 text-right">{t.balance_after}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
        </ScrollHint>
      </section>

      {/* Transfer modal */}
      {transferState.open && (
        <div className="fixed inset-0 bg-black/70 z-50 flex items-center justify-center p-4" onClick={() => setTransferState({ open: false, user: null, amount: '' })} data-testid="head-transfer-modal">
          <div className="bg-white dark:bg-[#111114] border border-slate-200 dark:border-zinc-800 rounded-xl w-full max-w-md p-5" onClick={e => e.stopPropagation()}>
            <h3 className="text-slate-900 dark:text-white font-semibold mb-1">Send Mu Points</h3>
            <p className="text-slate-500 dark:text-zinc-500 text-xs mb-4">To <span className="text-slate-600 dark:text-zinc-300">{transferState.user.email}</span> · your balance: <span className="text-amber-700 dark:text-amber-300">{me.balance} MP</span></p>
            <input type="number" min="1" placeholder="Amount in MP" value={transferState.amount} onChange={e => setTransferState(s => ({ ...s, amount: e.target.value }))} className="w-full bg-slate-50 dark:bg-[#0a0a0b] border border-slate-200 dark:border-zinc-800 rounded-lg px-3 py-2 text-sm text-slate-900 dark:text-white mb-3" data-testid="transfer-amount-input" autoFocus />
            <div className="flex gap-3">
              <button onClick={() => setTransferState({ open: false, user: null, amount: '' })} className="flex-1 py-2 bg-slate-100 dark:bg-zinc-800 text-slate-600 dark:text-zinc-300 rounded-lg text-sm">Cancel</button>
              <button onClick={doTransfer} className="flex-1 py-2 bg-slate-900 dark:bg-white text-white dark:text-black rounded-lg text-sm font-medium" data-testid="confirm-transfer-btn">Send</button>
            </div>
          </div>
        </div>
      )}

      {/* Payments history */}
      {payments.length > 0 && (
        <section className="mt-6" data-testid="recruiter-payments-section">
          <h2 className="text-sm font-semibold text-slate-600 dark:text-zinc-300 uppercase tracking-wider mb-2 flex items-center gap-2">
            <CreditCard className="w-3.5 h-3.5" /> Razorpay Recharge History
          </h2>
          <ScrollHint>
            <div className="overflow-x-auto bg-white dark:bg-[#111114] border border-slate-200 dark:border-zinc-800/50 rounded-lg">
              <table className="w-full text-xs min-w-[720px]" data-testid="recruiter-payments-table">
                <thead className="border-b border-slate-200 dark:border-zinc-800/70">
                  <tr className="text-slate-500 dark:text-zinc-500 text-left">
                    <th className="px-3 py-2 font-medium">Date</th>
                    <th className="px-3 py-2 font-medium">Amount paid</th>
                    <th className="px-3 py-2 font-medium">Mu credited</th>
                    <th className="px-3 py-2 font-medium">Rate</th>
                    <th className="px-3 py-2 font-medium">Razorpay Payment ID</th>
                    <th className="px-3 py-2 font-medium">Status</th>
                  </tr>
                </thead>
                <tbody>
                  {payments.map((p) => (
                    <tr key={p.id} className="border-b border-slate-200 dark:border-zinc-900/60" data-testid={`recruiter-payment-row-${p.id}`}>
                      <td className="px-3 py-2 text-slate-500 dark:text-zinc-400 whitespace-nowrap">
                        {p.created_at ? new Date(p.created_at).toLocaleString() : '—'}
                      </td>
                      <td className="px-3 py-2 text-slate-900 dark:text-white font-medium flex items-center gap-0.5">
                        <IndianRupee className="w-3 h-3" />{p.amount_inr}
                      </td>
                      <td className="px-3 py-2 text-emerald-600 dark:text-emerald-400 font-medium">+{p.mu_points_credited} MP</td>
                      <td className="px-3 py-2 text-slate-600 dark:text-zinc-300">₹{p.conversion_rate_inr_per_mu}/MP</td>
                      <td className="px-3 py-2 text-slate-600 dark:text-zinc-400 font-mono text-[11px] truncate max-w-[220px]" title={p.razorpay_payment_id}>{p.razorpay_payment_id}</td>
                      <td className="px-3 py-2">
                        <span className="text-[10px] uppercase tracking-wider px-2 py-0.5 rounded-full bg-emerald-500/10 text-emerald-600 dark:text-emerald-400">
                          {p.status || 'success'}
                        </span>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </ScrollHint>
        </section>
      )}

      <RazorpayCheckoutModal
        isOpen={rechargeOpen}
        onClose={() => setRechargeOpen(false)}
        user={{ email: user?.email, phone: user?.phone, name: user?.name }}
        onSuccess={() => load()}
      />
    </div>
  );
}
